import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';

function ShopDiscounts({ userData }) {
    const [discounts, setDiscounts] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [editingDiscount, setEditingDiscount] = useState(null);

    useEffect(() => {
        loadDiscounts();
        loadItems();
    }, []);

    const loadDiscounts = async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/admin/discounts`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) setDiscounts(data.discounts);
        } catch (error) {
            console.error('Error loading discounts:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadItems = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/admin/items`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) setItems(data.items);
        } catch (error) {
            console.error('Error loading items:', error);
        }
    };

    const deleteDiscount = async (discountId) => {
        if (!window.confirm('Are you sure you want to delete this discount?')) return;

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}shop/admin/discounts/${discountId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                window.dispatchEvent(new CustomEvent('showToast', {
                    detail: { message: 'Discount deleted successfully', type: 'success' }
                }));
                loadDiscounts();
            }
        } catch (error) {
            console.error('Error deleting discount:', error);
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="gold" header="Shop Discounts">
                <div className="flex justify-between items-center mb-4">
                    <p className="text-gray-700">Manage promotional discounts and sales.</p>
                    <button
                        onClick={() => {
                            setEditingDiscount(null);
                            setShowDiscountModal(true);
                        }}
                        className="bg-maroon hover:opacity-90 text-white px-4 py-2 font-['Jersey_10'] uppercase text-[14px] tracking-[1px]"
                        style={{ backgroundColor: '#5A2929', boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                    >
                        Add Discount
                    </button>
                </div>

                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                            <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-4 py-2 text-left">Item</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Type</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Value</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Start</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">End</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Active</th>
                                <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {discounts.map((discount) => (
                                <tr key={discount.DiscountID} className="hover:bg-gray-50">
                                    <td className="border border-gray-300 px-4 py-2">{discount.ItemName}</td>
                                    <td className="border border-gray-300 px-4 py-2">{discount.DiscountType}</td>
                                    <td className="border border-gray-300 px-4 py-2">{discount.DiscountValue}</td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        {new Date(discount.StartDate).toLocaleDateString()}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        {discount.EndDate ? new Date(discount.EndDate).toLocaleDateString() : 'No end'}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        {discount.IsActive ? 'Yes' : 'No'}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2">
                                        <button
                                            onClick={() => {
                                                setEditingDiscount(discount);
                                                setShowDiscountModal(true);
                                            }}
                                            className="text-blue-600 hover:underline mr-3"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteDiscount(discount.DiscountID)}
                                            className="text-red-600 hover:underline"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {showDiscountModal && (
                    <DiscountModal
                        discount={editingDiscount}
                        items={items}
                        onClose={() => setShowDiscountModal(false)}
                        onSave={() => {
                            setShowDiscountModal(false);
                            loadDiscounts();
                        }}
                    />
                )}
            </ContentBox>
        </AdminLayout>
    );
}

function DiscountModal({ discount, items, onClose, onSave }) {
    const [formData, setFormData] = useState({
        itemId: discount?.ItemID || '',
        discountType: discount?.DiscountType || 'percentage',
        discountValue: discount?.DiscountValue || 0,
        startDate: discount?.StartDate ? new Date(discount.StartDate).toISOString().slice(0, 16) : '',
        endDate: discount?.EndDate ? new Date(discount.EndDate).toISOString().slice(0, 16) : '',
        isActive: discount?.IsActive !== undefined ? discount.IsActive : 1
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const url = discount
                ? `${apiUrl}shop/admin/discounts/${discount.DiscountID}`
                : `${apiUrl}shop/admin/discounts`;

            const body = {
                ...formData,
                endDate: formData.endDate || null
            };

            const response = await fetch(url, {
                method: discount ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body)
            });

            const data = await response.json();
            if (data.success) {
                window.dispatchEvent(new CustomEvent('showToast', {
                    detail: { message: data.message, type: 'success' }
                }));
                onSave();
            }
        } catch (error) {
            console.error('Error saving discount:', error);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
            <div className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-xl w-full mx-4 z-[201]">
                <h2 className="text-maroon uppercase font-['Jersey_10'] text-[20px] tracking-[1px] mb-4">
                    {discount ? 'Edit Discount' : 'Add Discount'}
                </h2>
                <hr className="border-gold mb-4" />

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="col-span-2">
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Item
                            </label>
                            <select
                                value={formData.itemId}
                                onChange={(e) => setFormData({ ...formData, itemId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                required
                                disabled={!!discount}
                            >
                                <option value="">Select an item</option>
                                {items.map(item => (
                                    <option key={item.ItemID} value={item.ItemID}>
                                        {item.ItemName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Discount Type
                            </label>
                            <select
                                value={formData.discountType}
                                onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                            >
                                <option value="percentage">Percentage</option>
                                <option value="fixed">Fixed</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Value
                            </label>
                            <input
                                type="number"
                                value={formData.discountValue}
                                onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                step="0.1"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Start Date
                            </label>
                            <input
                                type="datetime-local"
                                value={formData.startDate}
                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                End Date (optional)
                            </label>
                            <input
                                type="datetime-local"
                                value={formData.endDate}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-gray-700 font-['Jersey_10'] uppercase text-[14px] mb-2">
                                Active
                            </label>
                            <select
                                value={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                            >
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex gap-3 justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[14px]"
                            style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-maroon hover:opacity-90 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[14px]"
                            style={{ backgroundColor: '#5A2929', boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ShopDiscounts;